// LLM Provider — live diagnostic view of the unified resolver.
// Shows which providers are configured, which is primary, rolling
// per-tier usage over the last 24 h, and the grading-retry queue.
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, RefreshCw, CheckCircle2, XCircle, Cpu, TrendingUp, AlertTriangle, PlayCircle, DollarSign, BellRing, ShieldAlert } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, Legend } from 'recharts';
import AdminLayout from './AdminLayout';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_LABELS = {
  openai_direct: 'OpenAI (direct)',
  anthropic_direct: 'Anthropic Claude (direct)',
  gemini_direct: 'Google Gemini (direct)',
  emergent_openai: 'Emergent → OpenAI',
  emergent_anthropic: 'Emergent → Claude',
  none: 'All tiers failed',
};

const TIER_ACCENT = {
  openai_direct: 'text-emerald-300',
  anthropic_direct: 'text-teal-300',
  gemini_direct: 'text-sky-300',
  emergent_openai: 'text-amber-300',
  emergent_anthropic: 'text-amber-300',
  none: 'text-red-300',
};

const ProviderCard = ({ label, model, available, isPrimary, tag, provider, token }) => {
  const [testing, setTesting] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const runTest = async () => {
    setTesting(true); setResult(null);
    try {
      const r = await axios.post(`${API}/admin/llm-provider/test/${provider}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setResult(r.data);
    } catch (e) {
      setResult({ ok: false, problem: e?.response?.data?.detail || 'Test failed', latency_ms: 0 });
    } finally { setTesting(false); }
  };
  return (
  <div
    className={`p-4 rounded-xl border ${available ? 'bg-white/[0.03] border-white/10' : 'bg-red-500/5 border-red-500/20'}`}
    data-testid={`llm-provider-card-${tag}`}
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="text-white/50 text-[10px] uppercase tracking-wider font-semibold">{label}</p>
        <p className="text-white font-semibold mt-0.5">{model}</p>
      </div>
      {available
        ? <CheckCircle2 className="w-5 h-5 text-emerald-400" />
        : <XCircle className="w-5 h-5 text-red-400" />
      }
    </div>
    <div className="mt-3 flex items-center gap-2 flex-wrap">
      {isPrimary && (
        <span className="text-[10px] uppercase tracking-wider font-semibold bg-teal-500/20 border border-teal-500/40 text-teal-200 px-2 py-0.5 rounded-full">
          Primary
        </span>
      )}
      <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full border ${available ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-200' : 'bg-red-500/20 border-red-500/40 text-red-200'}`}>
        {available ? 'Configured' : 'Missing key'}
      </span>
      {provider && available && (
        <button
          type="button"
          onClick={runTest}
          disabled={testing}
          className="ml-auto inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-white/80 hover:text-white bg-white/10 hover:bg-white/20 border border-white/20 rounded px-2 py-1 disabled:opacity-40"
          data-testid={`llm-test-${provider}`}
        >
          {testing ? '…' : 'Test'}
        </button>
      )}
    </div>
    {result && (
      <div
        className={`mt-3 rounded p-2 text-[11px] ${result.ok ? 'bg-emerald-500/10 text-emerald-100 border border-emerald-500/30' : 'bg-rose-500/10 text-rose-100 border border-rose-500/30'}`}
        data-testid={`llm-test-result-${provider}`}
      >
        {result.ok ? (
          <>
            <b>Working</b> · {Math.round(result.latency_ms)}ms · reply: <i>&ldquo;{result.reply}&rdquo;</i>
          </>
        ) : (
          <>
            <b>{result.problem || 'Failed'}</b>
            {result.problem_hint && <div className="text-rose-200/80 mt-0.5">{result.problem_hint}</div>}
          </>
        )}
      </div>
    )}
  </div>
  );
};

const fmtTokens = (n) => {
  if (!n) return '0';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
};
const fmtCost = (v) => {
  if (!v) return '$0.00';
  if (v < 0.01) return `$${v.toFixed(4)}`;
  return `$${v.toFixed(2)}`;
};

const _spendGroup = (tier) => {
  const t = String(tier || '');
  if (t.includes('anthropic')) return 'claude';
  if (t.includes('openai')) return 'openai';
  if (t.includes('gemini')) return 'gemini';
  return 'other';
};

// Daily spend tile — stacked $ per day by provider family, so the
// effect of moving workloads off Claude is visible at a glance.
const DailySpendChart = ({ rows }) => {
  const data = useMemo(() => {
    const byDay = {};
    (rows || []).forEach((r) => {
      const d = byDay[r.date] || (byDay[r.date] = { date: r.date, claude: 0, openai: 0, gemini: 0, other: 0 });
      d[_spendGroup(r.tier)] += r.est_cost_usd || 0;
    });
    return Object.values(byDay).sort((a, b) => a.date.localeCompare(b.date));
  }, [rows]);
  if (!data.length) return null;
  const total = data.reduce((s, d) => s + d.claude + d.openai + d.gemini + d.other, 0);
  const claudeTotal = data.reduce((s, d) => s + d.claude, 0);
  const claudePct = total ? Math.round((claudeTotal / total) * 100) : 0;
  return (
    <div className="mb-5" data-testid="llm-daily-spend-tile">
      <div className="flex items-center gap-2 mb-2">
        <p className="text-[11px] uppercase tracking-wider font-semibold text-white/60">Daily spend (est.)</p>
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-teal-500/15 border border-teal-500/30 text-teal-200" data-testid="llm-claude-share">
          Claude share: {claudePct}% of {fmtCost(total)}
        </span>
      </div>
      <ResponsiveContainer width="100%" height={180}>
        <BarChart data={data} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
          <XAxis dataKey="date" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
          <YAxis tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }} tickFormatter={(v) => `$${v}`} />
          <RTooltip
            formatter={(v, name) => [fmtCost(v), name]}
            contentStyle={{ background: '#0f172a', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8, fontSize: 11 }}
            labelStyle={{ color: 'rgba(255,255,255,0.7)' }}
          />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Bar dataKey="claude" name="Claude" stackId="s" fill="#2dd4bf" />
          <Bar dataKey="openai" name="OpenAI" stackId="s" fill="#34d399" />
          <Bar dataKey="gemini" name="Gemini" stackId="s" fill="#38bdf8" />
          <Bar dataKey="other" name="Other" stackId="s" fill="#a78bfa" radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

const TokenUsageSection = ({ usage }) => (
  <div className="bg-white/[0.02] border border-white/10 rounded-xl p-5" data-testid="llm-token-usage-card">
    <div className="flex items-center gap-2 mb-1">
      <DollarSign className="w-4 h-4 text-emerald-400" />
      <h2 className="text-white font-semibold">Tokens &amp; est. cost — last {usage?.days ?? 7} days</h2>
    </div>
    <p className="text-[11px] text-white/40 mb-4">{usage?.pricing_note}</p>
    <DailySpendChart rows={usage?.rows} />
    {usage?.totals?.length ? (
      <>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          {usage.totals.map(t => (
            <div key={t.tier} className="bg-white/[0.03] border border-white/10 rounded-lg p-3" data-testid={`llm-cost-total-${t.tier}`}>
              <p className={`text-[10px] uppercase tracking-wider font-semibold ${TIER_ACCENT[t.tier] || 'text-white/60'}`}>{TIER_LABELS[t.tier] || t.tier}</p>
              <p className="text-white text-lg font-bold mt-1">{fmtCost(t.est_cost_usd)}</p>
              <p className="text-[10px] text-white/40">{t.calls} calls · {t.success_pct}% ok · {fmtTokens(t.tokens_in)} in / {fmtTokens(t.tokens_out)} out</p>
              {(t.cache_read > 0 || t.cache_write > 0) && (
                <p className="text-[10px] text-sky-300/80" data-testid={`llm-cache-stats-${t.tier}`}>
                  cache: {fmtTokens(t.cache_read)} read / {fmtTokens(t.cache_write)} written · saved ~{fmtCost(t.cache_savings_usd)}
                </p>
              )}
            </div>
          ))}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="llm-token-usage-table">
            <thead>
              <tr className="text-left text-white/40 uppercase tracking-wider text-[10px] border-b border-white/5">
                <th className="py-2 pr-3">Day</th>
                <th className="py-2 pr-3">Provider</th>
                <th className="py-2 pr-3">Model</th>
                <th className="py-2 pr-3 text-right">Calls</th>
                <th className="py-2 pr-3 text-right">OK %</th>
                <th className="py-2 pr-3 text-right">Tokens in</th>
                <th className="py-2 pr-3 text-right">Tokens out</th>
                <th className="py-2 pr-0 text-right">Est. cost</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {usage.rows.map((r, i) => (
                <tr key={i}>
                  <td className="py-1.5 pr-3 text-white/60 font-mono">{r.date}</td>
                  <td className={`py-1.5 pr-3 font-semibold ${TIER_ACCENT[r.tier] || 'text-white/70'}`}>{TIER_LABELS[r.tier] || r.tier}</td>
                  <td className="py-1.5 pr-3 text-white/50">{r.model || '—'}</td>
                  <td className="py-1.5 pr-3 text-right text-white/80">{r.calls}</td>
                  <td className={`py-1.5 pr-3 text-right ${r.success_pct < 50 ? 'text-red-300' : 'text-white/60'}`}>{r.success_pct}%</td>
                  <td className="py-1.5 pr-3 text-right text-white/60">{fmtTokens(r.tokens_in)}</td>
                  <td className="py-1.5 pr-3 text-right text-white/60">{fmtTokens(r.tokens_out)}</td>
                  <td className="py-1.5 pr-0 text-right text-white font-semibold">{fmtCost(r.est_cost_usd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    ) : (
      <p className="text-white/40 text-sm italic">No LLM calls recorded yet in this window.</p>
    )}
  </div>
);

const SkipReasonsSection = ({ usage }) => (
  <div className="bg-white/[0.02] border border-white/10 rounded-xl p-5" data-testid="llm-skip-reasons-card">
    <div className="flex items-center gap-2 mb-1">
      <ShieldAlert className="w-4 h-4 text-rose-400" />
      <h2 className="text-white font-semibold">Why providers get skipped</h2>
    </div>
    <p className="text-[11px] text-white/40 mb-4">Failure reasons per direct provider over the same window — this is why traffic falls through the cascade.</p>
    {usage?.skip_reasons?.length ? (
      <div className="space-y-2">
        {usage.skip_reasons.map((r, i) => (
          <div key={i} className="bg-rose-500/5 border border-rose-500/20 rounded-lg px-3 py-2" data-testid={`llm-skip-reason-${r.tier}-${i}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="text-xs">
                <span className={`font-semibold ${TIER_ACCENT[r.tier] || 'text-white/70'}`}>{TIER_LABELS[r.tier] || r.tier}</span>
                <span className="text-rose-200 font-semibold ml-2">{r.problem}</span>
                <span className="text-white/40 ml-2">× {r.count}</span>
              </p>
              <p className="text-[10px] text-white/40">last seen {r.last_seen ? new Date(r.last_seen).toLocaleString() : '—'}</p>
            </div>
            <p className="text-[11px] text-white/50 mt-0.5">{r.hint}</p>
            <p className="text-[10px] text-white/30 font-mono mt-0.5 truncate" title={r.example_error}>{r.example_error}</p>
          </div>
        ))}
      </div>
    ) : (
      <p className="text-emerald-300/80 text-sm italic">No direct-provider failures in this window — the cascade never had to skip anyone.</p>
    )}
  </div>
);

const BudgetSection = ({ budget, token, onSaved }) => {
  const [cap, setCap] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  React.useEffect(() => { setCap(budget?.monthly_cap_usd ?? ''); }, [budget?.monthly_cap_usd]);
  const save = async (value) => {
    setSaving(true);
    try {
      const r = await axios.post(`${API}/admin/llm-provider/budget`, { monthly_cap_usd: value }, { headers: { Authorization: `Bearer ${token}` } });
      onSaved(r.data);
      toast.success(value ? `Monthly cap set to $${Number(value).toFixed(2)}` : 'Budget cap removed');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };
  const pct = budget?.pct_used ?? 0;
  const barPct = Math.min(pct, 100);
  const barColor = budget?.paused ? 'bg-red-400' : pct >= 80 ? 'bg-amber-400' : 'bg-emerald-400';
  return (
    <div className="bg-white/[0.02] border border-white/10 rounded-xl p-5" data-testid="llm-budget-card">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
        <div className="flex items-center gap-2">
          <ShieldAlert className="w-4 h-4 text-emerald-400" />
          <h2 className="text-white font-semibold">Budget guardrail</h2>
          {budget?.paused && (
            <span className="text-[10px] uppercase tracking-wider font-semibold bg-red-500/20 border border-red-500/40 text-red-200 px-2 py-0.5 rounded-full" data-testid="llm-budget-paused-badge">
              Cap hit — non-critical AI paused
            </span>
          )}
          {!budget?.paused && budget?.alerted_80 && (
            <span className="text-[10px] uppercase tracking-wider font-semibold bg-amber-500/20 border border-amber-500/40 text-amber-200 px-2 py-0.5 rounded-full">
              80% warning sent
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-white/40">Monthly cap $</span>
          <input
            type="number"
            min="0"
            step="1"
            value={cap}
            onChange={e => setCap(e.target.value)}
            placeholder="e.g. 100"
            className="w-24 bg-white/5 border border-white/15 rounded px-2 py-1 text-xs text-white placeholder-white/30 focus:outline-none focus:border-emerald-400/50"
            data-testid="llm-budget-input"
          />
          <button
            onClick={() => save(cap === '' ? null : cap)}
            disabled={saving}
            className="text-[11px] bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/40 text-emerald-200 rounded px-2.5 py-1 disabled:opacity-40"
            data-testid="llm-budget-save-btn"
          >
            {saving ? '…' : 'Save'}
          </button>
          {budget?.monthly_cap_usd ? (
            <button
              onClick={() => save(null)}
              disabled={saving}
              className="text-[11px] bg-white/5 hover:bg-white/10 border border-white/15 text-white/60 rounded px-2.5 py-1 disabled:opacity-40"
              data-testid="llm-budget-clear-btn"
            >
              Remove cap
            </button>
          ) : null}
        </div>
      </div>
      <p className="text-[11px] text-white/40 mb-3">
        Email at 80% used, and at 100% non-critical AI (assistants, quizzes, insights) pauses until the month rolls over. Call grading always keeps running.
      </p>
      {budget?.monthly_cap_usd ? (
        <div>
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-white/70" data-testid="llm-budget-spend">
              ${(budget.month_spend_usd ?? 0).toFixed(2)} of ${Number(budget.monthly_cap_usd).toFixed(2)} used this month ({pct}%)
            </span>
            <span className="text-white/40">{budget.month}</span>
          </div>
          <div className="bg-white/5 rounded-full h-2.5 overflow-hidden">
            <div className={`h-full ${barColor}`} style={{ width: `${barPct}%` }} />
          </div>
        </div>
      ) : (
        <p className="text-white/40 text-sm italic" data-testid="llm-budget-disabled">No cap set — AI spend is untracked against a budget. Set a monthly cap above to turn the guardrail on.</p>
      )}
    </div>
  );
};

const FEATURE_COLORS = ['bg-teal-400', 'bg-sky-400', 'bg-violet-400', 'bg-amber-400', 'bg-rose-400', 'bg-emerald-400', 'bg-indigo-400', 'bg-orange-400'];

const SpendByFeatureSection = ({ token }) => {
  const [data, setData] = React.useState(null);
  const [days, setDays] = React.useState(0);

  React.useEffect(() => {
    axios.get(`${API}/admin/llm-provider/spend-by-feature`, { params: days ? { days } : {}, headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setData(r.data))
      .catch(() => setData({ rows: [], total_cost_usd: 0 }));
  }, [token, days]);

  const rows = data?.rows || [];
  const maxCost = rows.length ? rows[0].cost_usd : 0;
  return (
    <div className="bg-white/[0.02] border border-white/10 rounded-xl p-5" data-testid="llm-spend-by-feature-card">
      <div className="flex items-center justify-between gap-3 mb-1">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-teal-400" />
          <h2 className="text-white font-semibold">Spend by feature</h2>
        </div>
        <div className="flex items-center gap-1.5">
          {[[0, 'This month'], [7, '7 days'], [30, '30 days']].map(([v, label]) => (
            <button key={v} onClick={() => setDays(v)}
              className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${days === v ? 'bg-teal-500/30 border border-teal-400/50 text-teal-100' : 'bg-white/5 border border-white/10 text-white/50 hover:text-white/80'}`}
              data-testid={`spend-feature-range-${v}`}>{label}</button>
          ))}
        </div>
      </div>
      <p className="text-[11px] text-white/40 mb-3">
        What's eating the AI budget — estimated cost per feature{data ? ` (total $${(data.total_cost_usd ?? 0).toFixed(2)})` : ''}.
      </p>
      {data === null ? (
        <Loader2 className="w-5 h-5 animate-spin text-teal-400 mx-auto my-4" />
      ) : rows.length === 0 ? (
        <p className="text-white/40 text-sm italic" data-testid="spend-feature-empty">No AI calls recorded in this window.</p>
      ) : (
        <div className="space-y-2" data-testid="spend-feature-rows">
          {rows.map((r, i) => (
            <div key={r.feature} data-testid={`spend-feature-row-${r.feature}`}>
              <div className="flex items-center justify-between text-xs mb-0.5">
                <span className="text-white/80 font-medium">{r.feature}</span>
                <span className="text-white/50">
                  ${r.cost_usd.toFixed(2)} · {r.pct}% · {r.calls.toLocaleString()} calls
                </span>
              </div>
              <div className="bg-white/5 rounded-full h-2 overflow-hidden">
                <div className={`h-full ${FEATURE_COLORS[i % FEATURE_COLORS.length]}`}
                  style={{ width: `${maxCost ? Math.max((r.cost_usd / maxCost) * 100, 2) : 2}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const AlertsSection = ({ usage, token, onSent, recips, onToggleRecipient, onAddCustom, onRemoveCustom }) => {
  const [sending, setSending] = React.useState(false);
  const [digestSending, setDigestSending] = React.useState(false);
  const [newEmail, setNewEmail] = React.useState('');
  const [adding, setAdding] = React.useState(false);
  const addCustom = async () => {
    if (!newEmail.trim()) return;
    setAdding(true);
    try {
      await onAddCustom(newEmail.trim());
      setNewEmail('');
    } finally { setAdding(false); }
  };
  const sendTest = async () => {
    setSending(true);
    try {
      const r = await axios.post(`${API}/admin/llm-provider/alerts/test`, {}, { headers: { Authorization: `Bearer ${token}` } });
      if (r.data.sent) {
        toast.success(`Test alert sent to ${r.data.recipients.join(', ')}${r.data.emails_enabled === false ? ' (dry-run — EMAILS_ENABLED=false)' : ''}`);
        onSent?.();
      } else {
        toast.error(r.data.reason || 'Test alert not sent');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test alert failed');
    } finally { setSending(false); }
  };
  const sendDigest = async () => {
    setDigestSending(true);
    try {
      const r = await axios.post(`${API}/admin/llm-provider/digest/send-now`, {}, { headers: { Authorization: `Bearer ${token}` } });
      if (r.data.sent) {
        toast.success(`Cost digest ($${(r.data.total_cost_usd ?? 0).toFixed(2)}) sent to ${r.data.recipients.join(', ')}`);
      } else {
        toast.error(r.data.reason || 'Digest not sent');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Digest send failed');
    } finally { setDigestSending(false); }
  };
  return (
    <div className="bg-white/[0.02] border border-white/10 rounded-xl p-5" data-testid="llm-alerts-card">
      <div className="flex items-center justify-between gap-3 mb-1">
        <div className="flex items-center gap-2">
          <BellRing className="w-4 h-4 text-amber-400" />
          <h2 className="text-white font-semibold">Provider alerts &amp; weekly digest</h2>
        </div>
        <button
          onClick={sendTest}
          disabled={sending}
          className="text-[11px] bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/40 text-amber-200 rounded px-2.5 py-1 inline-flex items-center gap-1.5 disabled:opacity-40"
          data-testid="llm-alert-test-btn"
        >
          {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <BellRing className="w-3 h-3" />}
          Send test alert
        </button>
      </div>
      <p className="text-[11px] text-white/40 mb-3">
        Recipients get an email when a direct provider fails 3 calls in a row (one alert per provider per 6 h), plus a weekly AI-spend digest every Monday 8:00 am Brisbane.
      </p>

      {/* Recipient management */}
      <div className="mb-4" data-testid="llm-alert-recipients">
        <p className="text-[10px] uppercase tracking-wider font-semibold text-white/50 mb-2">Email recipients</p>
        <div className="flex flex-wrap gap-2">
          {(recips?.recipients || []).map(r => (
            <div
              key={r.email}
              className={`flex items-center gap-2 text-xs rounded-full border px-3 py-1 ${r.excluded ? 'bg-white/[0.02] border-white/10 text-white/30' : r.custom ? 'bg-sky-500/10 border-sky-500/30 text-sky-100' : 'bg-emerald-500/10 border-emerald-500/30 text-emerald-100'}`}
              data-testid={`llm-recipient-${r.email}`}
            >
              <span className={r.excluded ? 'line-through' : ''}>{r.email}</span>
              {r.custom && <span className="text-[9px] uppercase tracking-wider text-sky-300/70">custom</span>}
              {r.custom ? (
                <button
                  onClick={() => onRemoveCustom(r.email)}
                  className="text-[10px] font-semibold uppercase tracking-wider rounded px-1.5 py-0.5 border bg-rose-500/20 border-rose-500/40 text-rose-200 hover:bg-rose-500/30"
                  data-testid={`llm-recipient-remove-custom-${r.email}`}
                >
                  Remove
                </button>
              ) : (
                <button
                  onClick={() => onToggleRecipient(r.email, !r.excluded)}
                  className={`text-[10px] font-semibold uppercase tracking-wider rounded px-1.5 py-0.5 border ${r.excluded ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/30' : 'bg-rose-500/20 border-rose-500/40 text-rose-200 hover:bg-rose-500/30'}`}
                  data-testid={`llm-recipient-toggle-${r.email}`}
                >
                  {r.excluded ? 'Re-add' : 'Remove'}
                </button>
              )}
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2 mt-2">
          <input
            type="email"
            value={newEmail}
            onChange={e => setNewEmail(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') addCustom(); }}
            placeholder="Add any email address…"
            className="w-56 bg-white/5 border border-white/15 rounded px-2.5 py-1 text-xs text-white placeholder-white/30 focus:outline-none focus:border-sky-400/50"
            data-testid="llm-recipient-add-input"
          />
          <button
            onClick={addCustom}
            disabled={adding || !newEmail.trim()}
            className="text-[11px] bg-sky-500/20 hover:bg-sky-500/30 border border-sky-500/40 text-sky-200 rounded px-2.5 py-1 disabled:opacity-40"
            data-testid="llm-recipient-add-btn"
          >
            {adding ? '…' : 'Add'}
          </button>
        </div>
        <p className="text-[10px] text-white/30 mt-1.5">Removing an email only stops alert + digest mail — it doesn&apos;t change their admin access. Custom addresses don&apos;t need an account.</p>
      </div>

      {/* Weekly digest */}
      <div className="flex items-center justify-between gap-3 bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 mb-4" data-testid="llm-digest-row">
        <p className="text-xs text-white/60">
          <span className="text-white/90 font-semibold">Weekly cost digest</span> — last week&apos;s spend per provider with trend arrows, Mondays 8:00 am Brisbane.
        </p>
        <button
          onClick={sendDigest}
          disabled={digestSending}
          className="text-[11px] bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/40 text-emerald-200 rounded px-2.5 py-1 inline-flex items-center gap-1.5 disabled:opacity-40 shrink-0"
          data-testid="llm-digest-send-btn"
        >
          {digestSending ? <Loader2 className="w-3 h-3 animate-spin" /> : <DollarSign className="w-3 h-3" />}
          Send now
        </button>
      </div>

      {usage?.recent_alerts?.length ? (
        <div className="space-y-1.5">
          {usage.recent_alerts.map((a, i) => (
            <div key={i} className="flex items-center justify-between gap-3 text-xs bg-white/[0.03] border border-white/10 rounded px-3 py-1.5" data-testid={`llm-alert-row-${i}`}>
              <p>
                <span className="text-amber-200 font-semibold">{a.label || a.tier}</span>
                <span className="text-white/40 ml-2 truncate">{a.err}</span>
              </p>
              <p className="text-[10px] text-white/40 shrink-0">{a.ts ? new Date(a.ts).toLocaleString() : '—'}</p>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-white/40 text-sm italic">No alerts sent yet — all direct providers have been healthy.</p>
      )}
    </div>
  );
};

const AdminLlmProviderPage = () => {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [info, setInfo] = useState(null);
  const [stats, setStats] = useState(null);
  const [queue, setQueue] = useState(null);
  const [usage, setUsage] = useState(null);
  const [recips, setRecips] = useState(null);
  const [budget, setBudget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [i, s, q, u, rc, b] = await Promise.all([
        axios.get(`${API}/admin/llm-provider`, { headers }),
        axios.get(`${API}/admin/llm-provider/stats?hours=24`, { headers }),
        axios.get(`${API}/admin/llm-provider/retry-queue?limit=50`, { headers }),
        axios.get(`${API}/admin/llm-provider/token-usage?days=14`, { headers }),
        axios.get(`${API}/admin/llm-provider/alert-recipients`, { headers }),
        axios.get(`${API}/admin/llm-provider/budget`, { headers }),
      ]);
      setInfo(i.data);
      setStats(s.data);
      setQueue(q.data);
      setUsage(u.data);
      setRecips(rc.data);
      setBudget(b.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load LLM diagnostics');
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  const toggleRecipient = async (email, excluded) => {
    try {
      const r = await axios.post(`${API}/admin/llm-provider/alert-recipients`, { email, excluded }, { headers });
      setRecips(r.data);
      toast.success(excluded ? `${email} removed from alert emails` : `${email} re-added to alert emails`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    }
  };

  const addCustomRecipient = async (email) => {
    try {
      const r = await axios.post(`${API}/admin/llm-provider/alert-recipients/custom`, { email }, { headers });
      setRecips(r.data);
      toast.success(`${email} added to alert emails`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Add failed');
    }
  };

  const removeCustomRecipient = async (email) => {
    try {
      const r = await axios.delete(`${API}/admin/llm-provider/alert-recipients/custom/${encodeURIComponent(email)}`, { headers });
      setRecips(r.data);
      toast.success(`${email} removed`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Remove failed');
    }
  };

  const retryOne = async (callId) => {
    setRetrying(callId);
    try {
      const r = await axios.post(`${API}/admin/llm-provider/retry-queue/${callId}/retry`, {}, { headers });
      toast.success(`Retried ${callId} — score ${r.data.overall_score}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Retry failed');
    } finally {
      setRetrying(null);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="llm-provider-page">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <Cpu className="w-6 h-6 text-teal-400" />
              <h1 className="text-2xl font-bold text-white">LLM Provider</h1>
            </div>
            <p className="text-white/40 text-sm mt-0.5">
              Direct providers run as a failover cascade — primary first, then Gemini, then the other direct key. Emergent is the safety-net tier — every call there costs you.
            </p>
          </div>
          <button
            onClick={load}
            className="text-xs bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 rounded-lg px-3 py-1.5 inline-flex items-center gap-1.5"
            data-testid="llm-provider-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {loading && !info ? (
          <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 text-white/30 animate-spin" /></div>
        ) : (
          <>
            {/* Provider cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <ProviderCard
                label="OpenAI (direct)"
                model={info?.openai_model || '—'}
                available={info?.openai_direct_available}
                isPrimary={info?.primary === 'openai'}
                tag="openai"
                provider="openai"
                token={token}
              />
              <ProviderCard
                label="Anthropic (direct)"
                model={info?.anthropic_model || '—'}
                available={info?.anthropic_direct_available}
                isPrimary={info?.primary === 'anthropic'}
                tag="anthropic"
                provider="anthropic"
                token={token}
              />
              <ProviderCard
                label="Gemini (secondary)"
                model={info?.gemini_model || '—'}
                available={info?.gemini_direct_available}
                isPrimary={false}
                tag="gemini"
                provider="gemini"
                token={token}
              />
              <ProviderCard
                label="Emergent (safety net)"
                model="Universal Key proxy"
                available={info?.emergent_available}
                isPrimary={false}
                tag="emergent"
                provider="emergent"
                token={token}
              />
            </div>

            {/* Usage stats */}
            <div className="bg-white/[0.02] border border-white/10 rounded-xl p-5" data-testid="llm-usage-card">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-teal-400" />
                  <h2 className="text-white font-semibold">Usage — last 24 h</h2>
                </div>
                <p className="text-xs text-white/40">
                  Total calls: <span className="text-white/80 font-semibold">{stats?.total_calls ?? 0}</span>
                </p>
              </div>
              {stats?.emergent_share_pct > 0 && (
                <div className="mb-4 bg-amber-500/10 border border-amber-500/30 text-amber-200 rounded-lg px-3 py-2 text-xs">
                  <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
                  <strong>{stats.emergent_share_pct}%</strong> of calls fell through to Emergent — that&apos;s traffic you&apos;re paying for. Check that OpenAI and Anthropic keys are healthy.
                </div>
              )}
              {stats?.by_tier?.length ? (
                <div className="space-y-2">
                  {stats.by_tier.map(row => (
                    <div key={row.tier} className="flex items-center gap-3" data-testid={`llm-tier-row-${row.tier}`}>
                      <div className="w-40 shrink-0">
                        <p className={`text-xs font-semibold ${TIER_ACCENT[row.tier] || 'text-white/70'}`}>
                          {TIER_LABELS[row.tier] || row.tier}
                        </p>
                        <p className="text-[10px] text-white/40">{row.count} calls · {row.avg_ms} ms avg · {row.success_pct}% ok</p>
                      </div>
                      <div className="flex-1 bg-white/5 rounded-full h-2 overflow-hidden">
                        <div
                          className={`h-full ${row.tier.startsWith('emergent') ? 'bg-amber-400' : row.tier === 'openai_direct' ? 'bg-emerald-400' : row.tier === 'anthropic_direct' ? 'bg-teal-400' : row.tier === 'gemini_direct' ? 'bg-sky-400' : 'bg-red-400'}`}
                          style={{ width: `${row.share_pct}%` }}
                        />
                      </div>
                      <p className="w-14 text-right text-xs text-white/80 font-semibold shrink-0">{row.share_pct}%</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-white/40 text-sm italic">No LLM calls recorded yet in this window.</p>
              )}
            </div>

            {/* Token usage + cost */}
            <TokenUsageSection usage={usage} />

            {/* Skip reasons */}
            <SkipReasonsSection usage={usage} />

            {/* Budget guardrail */}
            <BudgetSection budget={budget} token={token} onSaved={setBudget} />

            <SpendByFeatureSection token={token} />

            {/* Alerts */}
            <AlertsSection usage={usage} token={token} onSent={load} recips={recips} onToggleRecipient={toggleRecipient} onAddCustom={addCustomRecipient} onRemoveCustom={removeCustomRecipient} />

            {/* Retry queue */}
            <div className="bg-white/[0.02] border border-white/10 rounded-xl p-5" data-testid="llm-retry-queue-card">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h2 className="text-white font-semibold">Grading retry queue</h2>
                  <p className="text-xs text-white/40 mt-0.5">
                    Calls whose grading exhausted every LLM tier. The scheduler auto-retries every 5 min with exponential backoff (5, 15, 45, 135 min…).
                  </p>
                </div>
                <span className="text-[10px] uppercase tracking-wider font-semibold bg-white/5 border border-white/10 text-white/70 px-2 py-0.5 rounded-full">
                  {queue?.total_pending ?? 0} pending
                </span>
              </div>
              {queue?.items?.length ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-white/40 uppercase tracking-wider text-[10px] border-b border-white/5">
                        <th className="py-2 pr-3">Call ID</th>
                        <th className="py-2 pr-3">Attempts</th>
                        <th className="py-2 pr-3">Next retry</th>
                        <th className="py-2 pr-3">Last error</th>
                        <th className="py-2 pr-3"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {queue.items.map(row => (
                        <tr key={row.call_id} data-testid={`llm-retry-row-${row.call_id}`}>
                          <td className="py-2 pr-3 text-white/80 font-mono">{row.call_id}</td>
                          <td className="py-2 pr-3 text-white/60">{row.attempts ?? 0}</td>
                          <td className="py-2 pr-3 text-white/60">{row.next_retry_at ? new Date(row.next_retry_at).toLocaleString() : '—'}</td>
                          <td className="py-2 pr-3 text-red-300 max-w-xs truncate" title={row.last_error}>{row.last_error || '—'}</td>
                          <td className="py-2 pr-3 text-right">
                            <button
                              onClick={() => retryOne(row.call_id)}
                              disabled={retrying === row.call_id}
                              className="text-[11px] bg-teal-500/20 hover:bg-teal-500/30 border border-teal-500/40 text-teal-200 rounded px-2 py-1 inline-flex items-center gap-1 disabled:opacity-40"
                              data-testid={`llm-retry-btn-${row.call_id}`}
                            >
                              {retrying === row.call_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <PlayCircle className="w-3 h-3" />}
                              Retry now
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-white/40 text-sm italic">Queue is clean — every recent grading resolved on the first try.</p>
              )}
            </div>

            {/* Hint */}
            <div className="text-[11px] text-white/40 italic">{info?.hint}</div>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminLlmProviderPage;
